/**
 * Folders (docs/FOLDERS.md) as the app works with them: where an items list is (a folder, or the root) and whether it
 * lists what's under it flat or a level at a time, every folder the viewer sees (for breadcrumbs, pickers and
 * search), and the small rules the dialogs follow (a free name, whether a change of sharing narrows who sees it).
 */
import { signal } from "@preact/signals";
import { api } from "../api/client.ts";
import type { Crumb, FolderList, FolderRow, SpaceOut, Visibility } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { spaceApi, spaceUrl } from "./paths.ts";
import { type Resource, useResource } from "./resource.ts";
import { store, stored } from "./storage.ts";
import { fold } from "./text.ts";

/** Lists scoped by a link or a thread: one set of items, listed flat as before folders. */
const SCOPED = ["thread", "refers_to", "linked_from"];
/** What a folder's link leaves out of the list it's in: where it was, and what was only for that place. */
const LEFT_BEHIND = ["cursor", "folder", "tab", "q", "deep"];
/** How many folders "Move to…" remembers as recent. */
const RECENT = 5;
const RECENT_KEY = "revisa.folders.recent.";
const ZIP = /\.zip$/iu;
const ZIP_MIMES: ReadonlySet<string> = new Set(["application/zip", "application/x-zip-compressed"]);

/** Every folder the viewer sees, and each by its id. */
interface FolderIndex {
  rows: readonly FolderRow[];
  byId: ReadonlyMap<string, FolderRow>;
}

/** Who sees something, as items and folders both say it. */
interface Sharing {
  visibility: Visibility;
  private_to?: readonly string[] | null | undefined;
  guests?: readonly string[] | null | undefined;
}

/** A folder hidden from the viewer, in a breadcrumb: neither its name nor its id. */
const HIDDEN: Crumb = { hidden: true, name: "…" };

const setOf = (xs: readonly string[] | null | undefined) => new Set(xs ?? []);
const sameSet = (a: ReadonlySet<string>, b: ReadonlySet<string>) => a.size === b.size && [...a].every((x) => b.has(x));

/**
 * A search lists what it finds flat (each item with its breadcrumb, folders named like it on top) rather than folder
 * by folder; false lists it by folder. Either way "Flatten subfolders" switches it.
 */
const SEARCH_FLAT = true;

/** Where an items list is, and how it lists it. */
export interface Place {
  /** The folder shown (none: the root). */
  folder: string | null;
  /** Everything under it, flat; or (false) its own items, its subfolders first. */
  flat: boolean;
}

/** The list's place from its URL: `folder`, and `deep` as asked (else flat for a search or a scoped list). */
export function placeOf(sp: URLSearchParams): Place {
  const asked = sp.get("deep");
  const byDefault = sp.get("q")?.trim() ? SEARCH_FLAT : SCOPED.some((k) => sp.has(k));
  return { folder: sp.get("folder"), flat: asked === null ? byDefault : asked === "true" };
}

/** The items query of a place: a folder's own items (`root`: those in none), or everything under it. */
export function placeQuery(p: Place): { folder: string | null; deep: string | null } {
  if (p.flat) {
    return p.folder ? { folder: p.folder, deep: "true" } : { folder: null, deep: null };
  }
  return { folder: p.folder ?? "root", deep: null };
}

/** A folder's page (none: the root's), keeping the list's filters but what belonged to where it was. */
export function folderHref(space: Pick<SpaceOut, "id">, id: string | null, from?: URLSearchParams): string {
  const q = new URLSearchParams(from);
  for (const k of LEFT_BEHIND) {
    q.delete(k);
  }
  if (id) {
    q.set("folder", id);
  }
  const s = q.toString();
  return spaceUrl(space, s ? `/items?${s}` : "/items");
}

/** Every folder the viewer sees (none while it loads, or when the server has no folders to say). */
export function useFolderIndex(space: Pick<SpaceOut, "id">): Resource<FolderIndex> {
  return useResource<FolderIndex>(`folders:${space.id}`, (abort) => folderIndex(space, abort));
}

/** Every folder the viewer sees, asked now. */
export async function folderIndex(space: Pick<SpaceOut, "id">, abort?: AbortSignal): Promise<FolderIndex> {
  const list = await api.get<FolderList>(spaceApi(space, "/folders"), { deep: true }, abort);
  return { rows: list.data, byId: new Map(list.data.map((f) => [f.id, f])) };
}

/**
 * Folder `id`'s breadcrumb from the root, itself last: a folder hidden from the viewer between two they see (or above
 * the first) is "…". One they don't see at all is "…" alone.
 */
export function crumbsOf(index: FolderIndex | undefined, id: string | null | undefined): Crumb[] {
  if (!id) {
    return [];
  }
  let at = index?.byId.get(id);
  if (!at) {
    return [HIDDEN];
  }
  const out: Crumb[] = [];
  // At most every folder once (a loop in what the server said can't hang the page).
  const most = (index?.rows.length ?? 0) + 1;
  for (let n = 0; at && n < most; n += 1) {
    out.unshift({ id: at.id, name: at.name });
    if (at.parent && at.parent !== at.under) {
      out.unshift(HIDDEN);
    }
    at = at.under ? index?.byId.get(at.under) : undefined;
  }
  return out;
}

/** A breadcrumb as words: "Clientes / Acme". */
export const pathText = (crumbs: readonly Crumb[]): string => crumbs.map((c) => c.name).join(" / ");

/** Whether `inside` is folder `id` or one under it. */
export function isWithin(index: FolderIndex | undefined, inside: string, id: string): boolean {
  return crumbsOf(index, inside).some((c) => c.id === id);
}

/** The folders whose own names hold `q` (accents and case aside), under `within` when given (none: anywhere). */
export function namedLike(index: FolderIndex | undefined, q: string, within: string | null): FolderRow[] {
  const needle = fold(q.trim());
  if (!needle) {
    return [];
  }
  return (index?.rows ?? []).filter(
    (f) => fold(f.name).includes(needle) && f.id !== within && (!within || isWithin(index, f.id, within)),
  );
}

/** The name `name` numbered, as a clash is resolved: "Acme (2)". */
export const numbered = (name: string, n: number): string => `${name} (${n})`;

/** The first of "Acme (2)", "Acme (3)"… that none of `taken` is (case and accents aside). */
export function freeName(name: string, taken: readonly string[]): string {
  const used = new Set(taken.map((x) => fold(x.trim())));
  let n = 2;
  while (used.has(fold(numbered(name, n)))) {
    n += 1;
  }
  return numbered(name, n);
}

/** Whether `a` and `b` are seen by the same people: the same visibility, list (while private) and guests. */
export function sameSharing(a: Sharing, b: Sharing): boolean {
  if (a.visibility !== b.visibility || !sameSet(setOf(a.guests), setOf(b.guests))) {
    return false;
  }
  return a.visibility !== "private" || sameSet(setOf(a.private_to), setOf(b.private_to));
}

/**
 * Whether going from `before` to `after` takes anyone away: no longer public, made private, people taken off its
 * list, guests taken off. Adding people, or opening it up, widens it.
 */
export function narrows(before: Sharing, after: Sharing): boolean {
  if (before.visibility === "public" && after.visibility !== "public") {
    return true;
  }
  if (after.visibility === "private" && before.visibility !== "private") {
    return true;
  }
  const lost = (was: readonly string[] | null | undefined, now: readonly string[] | null | undefined) =>
    [...setOf(was)].some((x) => !setOf(now).has(x));
  return (
    (after.visibility === "private" && lost(before.private_to, after.private_to)) || lost(before.guests, after.guests)
  );
}

/** `before` with `change` made: made private, `me` is on its list (as the server puts them there). */
export function applied(
  before: Sharing,
  change: { visibility?: Visibility | null; private_to?: readonly string[] | null; guests?: readonly string[] | null },
  me: string | undefined,
): Sharing {
  const visibility = change.visibility ?? before.visibility;
  const people = [...(change.private_to ?? before.private_to ?? [])];
  if (me && visibility === "private" && change.visibility === "private" && !people.includes(me)) {
    people.push(me);
  }
  return { visibility, private_to: people, guests: change.guests ?? before.guests ?? [] };
}

/** What a folder holds, in words: "3 folders and 41 items", "2 items" (nothing: empty). */
export function whatInside(c: { folders: number; items: number }): string {
  const parts = [
    c.folders > 0 ? t("folders.count.folders", { count: c.folders }) : null,
    c.items > 0 ? t("folders.count.items", { count: c.items }) : null,
  ].filter((x): x is string => x !== null);
  const [a = "", b] = parts;
  return b === undefined ? a : t("folders.count.and", { a, b });
}

/** Whether an item is a zip file (to unpack). */
export function isZipItem(item: { file?: { mime: string; name?: string | null } | null; title?: string }): boolean {
  const name = item.file?.name ?? item.title ?? "";
  return Boolean(item.file && (ZIP_MIMES.has(item.file.mime) || ZIP.test(name)));
}

/** The folders moved into lately in `space`, the latest first. */
export function recentFolders(space: Pick<SpaceOut, "id">): string[] {
  try {
    const ids: unknown = JSON.parse(stored(RECENT_KEY + space.id) ?? "[]");
    return Array.isArray(ids) ? ids.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

/** Remembers folder `id` as moved into just now (in this browser). */
export function rememberFolder(space: Pick<SpaceOut, "id">, id: string): void {
  const ids = [id, ...recentFolders(space).filter((x) => x !== id)].slice(0, RECENT);
  store(RECENT_KEY + space.id, JSON.stringify(ids));
}

/** A folder's id (`dir_…`), not an item's. */
export const isFolderId = (id: string): boolean => id.startsWith("dir_");

/** A board grouped by folder (its `group=`), not by a field. */
export const FOLDER_GROUP = "@folder";

/** Folders made in this page's life: listed where they were made, even when a filter would leave them out. */
export const madeFolders = signal<ReadonlySet<string>>(new Set());

/** Remembers folder `id` as made just now. */
export function rememberMade(id: string): void {
  madeFolders.value = new Set([...madeFolders.value, id]);
}

export type { FolderIndex, Sharing };
